import { List, Map, Set } from 'immutable';
import { dispatch, registerHandler } from 'nike-re-framejs';

import { fireGAEvent } from '@/analytics';
import { deepMerge } from 'nike-ui/src/common/immutable/merge';
import { defaultMiddlewares, isString } from '@/common/util';
import i18n from '@i18n';
import { format, parse } from '@/keyboard/Keyboard';
import { setKeepBetsSettings } from '@/services/localStorageData';
import { cmdStream$ } from './command';
import { selectNikeOneBetslip, selectVirtualBetslip } from '@/betslip/commands';
import { mergePushMessage } from '@/betslip/merge';
import { BetslipPick } from '@/betslip/types';
import { updateURLSearchParams } from '@/routing';

const TIP_SLIDE_TOGGLE = {
    slidedown: 'slideup',
    slideup: 'slidedown'
};

const UI_VALIDATION_HANDLERS = ['ReloadPage'];

export function sendCmd(cmd) {
    cmdStream$.next(cmd);
}

function isUIValidationHandler(cmdName) {
    return UI_VALIDATION_HANDLERS.indexOf(cmdName) > -1;
}

// tato funkcia je hnus a des
// odporucam do nej vobec nesahat
// ale keby predsa niekoho zaujimalo o co tu ide tak nech sa paci:
// - updatuju sa tu hodnoty v tiket editoroch (vyska vkladu peniaze) zo servera
// (ale len v pripade ze nie su schedulnute dalsie commandy na odoslanie -> to zabezpecuje ze v pripade pomalej siete
// sa neprepisuje navzajom hodnota zo servra hodnotou ktoru prepisuje user)
// rozpracovane hodnoty v editore su na pathoch
// ['betslip', 'stake', 'Standard', 'value']
// ['betslip', 'stake', 'Combination1', 'value']
// ['betslip', 'stake', 'Combination2', 'value']
// ['betslip', 'stake', 'Combination...', 'value']
// k updatu dojde len pokial je numericka hodnota ina, ako je v tiket editore
function updateBetslipEditorStakes(db) {
    // pokial su schedulnute dalsie betslip commandy, tak neupdatujeme hodnoty v tiket editore
    if (db.getIn(['betslip', 'command', 'isAnyInProgress'])) {
        return db;
    }
    return db
        .getIn(['server', 'betslip', 'stake', 'stakes'], Map())
        .reduce(
            (newDbNg, stake, stakeType) =>
                newDbNg.updateIn(['betslip', 'stake', stakeType, 'value'], value => {
                    // update Standard
                    // vzdy sa prepisuje server hodnotou, pokial je server hodnota rozdielna od lokalnej
                    if (stakeType === 'Standard') {
                        if (isString(value)) {
                            let parsedLocal = parse(value);
                            parsedLocal = parsedLocal < 0.5 ? 0.5 : parsedLocal;
                            if (parsedLocal === stake.get('money')) {
                                return value;
                            }
                        }
                        return format(stake.get('money') < 0.5 ? 0.5 : stake.get('money'));
                    }

                    // update CombinationX
                    // pokial je na tikete chyba COMBINATION_STAKE_ERROR_SYSTEM_STAKE, neprepisujeme server hodnotou
                    if (isString(value)) {
                        let parsedLocal = parse(value);
                        if (parsedLocal === stake.get('money')) {
                            return value;
                        }
                    }
                    if (db.getIn(['serverError', 'type']) === 'COMBINATION_STAKE_ERROR_SYSTEM_STAKE') {
                        return value;
                    }
                    return format(stake.get('money'));
                }),
            db
        )
        .updateIn(['betslip', 'stake', 'StandardPoints', 'value'], value => {
            const bodPoints = db.getIn(['server', 'betslip', 'totals', 'spent', 'bodPoints']);
            return bodPoints ? bodPoints.toString() : value;
        });
}

registerHandler('refreshBetslip', defaultMiddlewares, (db, [betslip]) => {
    if (betslip.get('messageType') === 'PUSH') {
        return db.updateIn(['server'], existing => mergePushMessage(existing, betslip));
    }

    return db
        .updateIn(['server'], existing => deepMerge(existing, betslip))
        .update(db => db.set('serverError', db.getIn(['server', 'lastCmdError'])))
        .update(updateBetslipEditorStakes);
});

registerHandler('updateLastCmdError', defaultMiddlewares, (db, [lastCmdError]) =>
    db.setIn(['serverError'], lastCmdError)
);

registerHandler('setServerError', defaultMiddlewares, (db, [errorCode]) => {
    const code2key = {
        ERROR_SERVER: 'contactus.error.server'
    };
    return db.setIn(
        ['serverError'],
        Map({
            type: errorCode,
            localizedType: i18n(code2key[errorCode] || errorCode),
            context: {},
            commands: List()
        })
    );
});

registerHandler('changeSystemStake', defaultMiddlewares, (db, [type, money]) => {
    sendCmd([
        'ChangeSystemStake',
        {
            stakeType: type,
            amount: money
        }
    ]);
    return db; //.setIn(['server', 'betslip', 'stake', 'stakes', type, 'money'], money);
});

registerHandler('changeOverallMoneyStake', defaultMiddlewares, (db, [money, type]) => {
    sendCmd([
        'ChangeOverallMoneyStake',
        {
            amount: money
        }
    ]);
    if (type) {
        return db.setIn(['betslip', 'lastChange'], type);
    }
    return db; //.setIn(['server', 'betslip', 'stake', 'money'], money);
});

registerHandler('changeAdvantagePercentage', defaultMiddlewares, (db, [percentage]) => {
    const multiplier = (100 + parseInt(percentage, 10)) / 100;

    sendCmd(multiplier > 1 ? ['ApplyOddsAdvantage', { multiplier }] : ['RemoveExtra', {}]);

    return db;
});

registerHandler('cmd', defaultMiddlewares, (db, [cmd]) => {
    sendCmd(cmd);
    return db;
});

registerHandler('betslip.cmd', defaultMiddlewares, (db, [cmd]) => {
    sendCmd(cmd);
    return db;
});

registerHandler('validationCmd', defaultMiddlewares, (db, [cmd]) => {
    const dispatchedCmd = isUIValidationHandler(cmd.first()) ? cmd.toJS() : ['cmd', cmd.toJS()];
    dispatch(dispatchedCmd);
    return db;
});

export const togglePick = (pick: BetslipPick) => db => {
    const selectionId = pick.betId + '-' + pick.selectionCode;
    const currentUserId = db.getIn(['account', 'userData', 'cisloZmluvy']);
    const selected = db.getIn(['server', 'betslip', 'pickIds'], Set()).contains(selectionId);
    if (currentUserId === pick.betbookContractId) {
        delete pick.betbookContractId;
        delete pick.betbookId;
    }
    if (selected) {
        sendCmd(['RemovePick', { betId: pick.betId }]);
    } else {
        sendCmd(['AddPick', pick]);
    }
    return db;
};

registerHandler('addPick', defaultMiddlewares, (db, [pick]) => {
    sendCmd(['AddPickMobile', pick]);
    return db;
});

registerHandler('setOverallStake', defaultMiddlewares, (db, [type, value]) =>
    db.setIn(['server', 'betslip', 'stake', type], value)
);

registerHandler('toggleTipPopup', defaultMiddlewares, (db, [betId, toggleClass, direction]) => {
    const betslipState = db.get('betslipTipState');
    let newDb = db;

    if (
        newDb.getIn(['betslipTipState', betId, 'state']) === TIP_SLIDE_TOGGLE.slideup &&
        newDb.getIn(['betslipTipState', betId, 'direction']) !== direction
    ) {
        return newDb.setIn(['betslipTipState', betId, 'direction'], direction);
    }
    if (betslipState) {
        betslipState.forEach((item, actId) => {
            if (item.get('state') === TIP_SLIDE_TOGGLE.slideup) {
                newDb = db.setIn(['betslipTipState', actId, 'state'], TIP_SLIDE_TOGGLE.slidedown);
            }
        });
    }
    return newDb.setIn(['betslipTipState', betId], Map({ state: TIP_SLIDE_TOGGLE[toggleClass], direction }));
});

registerHandler('confirmCmdError', defaultMiddlewares, db => db.setIn(['serverError'], null));

registerHandler('setAnimationEnd', defaultMiddlewares, db => db.updateIn(['animationCounter'], 0, old => old + 1));

registerHandler('submitBet', defaultMiddlewares, (db, [cmd]) => {
    sendCmd(cmd);
    return db.setIn(['betslip', 'lastChange'], 'NoChange').setIn(['betslip', 'submitting'], new Date().getTime());
});
registerHandler('betslip/changeKeepBets', defaultMiddlewares, (db, [value]) => {
    setKeepBetsSettings(value);
    fireGAEvent('Editor tiketu', 'Ponechat', value ? 'on' : 'off');
    return db.setIn(['betslip', 'keepBets'], value);
});

registerHandler('betslip/onEnter', defaultMiddlewares, db => {
    return db.setIn(['betslip', 'open'], true);
});

registerHandler('betslip/onLeave', defaultMiddlewares, db => {
    return db.setIn(['betslip', 'open'], false);
});

export function betslipOpenEditor() {
    updateURLSearchParams({ tiket: null });
}

export function betslipCloseEditor() {
    //updateURLSearchParams({ tiket: undefined });
    dispatch(['history_back']);
}

export function betslipToggleQuickbet() {
    dispatch(['betslip.toggleQuickbet']);
}

let scheduleSwapBetslipTimer = null;
registerHandler('betslip.ensureNikeOneBetslip', defaultMiddlewares, db => {
    if (!db.getIn(['server'])) {
        scheduleSwapBetslipTimer = setTimeout(() => dispatch(['betslip.ensureNikeOneBetslip']), 200);
        return db;
    }
    if (db.getIn(['server', 'betslipIndex']) !== 0) {
        sendCmd(selectNikeOneBetslip()[1]);
    }
    return db;
});
registerHandler('betslip.virtualGamesBetslip', defaultMiddlewares, db => {
    if (scheduleSwapBetslipTimer) {
        clearTimeout(scheduleSwapBetslipTimer);
    }

    if (db.getIn(['server', 'betslipIndex']) !== 1) {
        sendCmd(selectVirtualBetslip()[1]);
    }
    return db;
});

registerHandler('betslip.toggleQuickbet', defaultMiddlewares, db => {
    const toggleValue = db.getIn(['betslip', 'quickbet']);
    fireGAEvent('RychlePodanie', 'button', toggleValue ? 'off' : 'on');
    return db.updateIn(['betslip', 'quickbet'], value => !value);
});

/**
 * handler sa moze volat v troch stavoch
 * [true, 'xxx', Array(0)] - vykonava sa command 'xxx'
 * [true, undefined, Array(1)] - vykonava sa stale predchadzajuci command, naplanovany je dalsi
 */
registerHandler('betslip/setCommandInProgress', defaultMiddlewares, (db, [inProgress, next, waitArray]) =>
    db
        .setIn(['betslip', 'command', 'isAnyInProgress'], inProgress)
        .updateIn(['betslip', 'command'], command => {
            if (!inProgress) {
                return command.delete('run');
            }
            return next ? command.set('run', next[0]) : command;
        })
        .updateIn(['betslip', 'command'], command => {
            // ak running + waiting obsahuje aspon 1 command iny ako 'RefreshBetslip/PollBetslip'
            const scheduled = waitArray
                .map(wait => wait[0])
                .concat(command.get('run'))
                .filter(Boolean);
            const inProgressUser =
                scheduled.filter(cmd => cmd !== 'RefreshBetslip' && cmd !== 'PollBetslip').length > 0;
            return command.set('isUserInProgress', inProgressUser);
        })
);

registerHandler('betslip/setPlaceButtonIntersecting', defaultMiddlewares, (db, [isIntersecting]) =>
    db.setIn(['betslip', 'intersectingPlaceButton'], isIntersecting)
);
